import React, { useState } from 'react';
import { CheckCircle2, IdCard, Link2, ScanFace, XCircle } from 'lucide-react';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { LoadingSpinner } from '../components/ui/loading-states';
import { Pagination } from '../components/ui/pagination';
import { useBioTerminalCards, useBioTerminals } from '../hooks/useBio';
import { MapTerminalCardDialog } from '../components/bio/MapTerminalCardDialog';
import { usePermissions } from '../hooks/usePermissions';
import type {
  BioTerminal,
  BioTerminalCard,
  BioTerminalCardEntry,
  MapBioTerminalCardResult,
} from '../types/bio.types';

const PAGE_SIZE = 25;

const formatDate = (value: string | null): string =>
  value ? new Date(value).toLocaleString('ru-RU') : '—';

const employeeName = (card: BioTerminalCard): string => {
  if (!card.employee) return '';
  const { last_name, first_name, middle_name } = card.employee;
  return [last_name, first_name, middle_name].filter(Boolean).join(' ');
};

/** Хвост серийника: у терминалов одной модели различается только он. */
const serialTail = (serial: string | undefined): string =>
  serial ? `…${serial.slice(-4)}` : '—';

/** Подпись терминала: модель одинакова у входа и выхода — нужен серийник. */
const terminalLabel = (terminal: BioTerminal): string =>
  terminal.model
    ? `${terminal.model} · ${serialTail(terminal.serial_number)}`
    : terminal.serial_number;

/** Значок терминала в строке человека: сопоставлена / нет / удалена. */
function TerminalCardBadge({ entry }: { entry: BioTerminalCardEntry }) {
  const serial = entry.terminal?.serial_number;
  if (entry.missing_since) {
    return (
      <Badge
        variant="outline"
        className="gap-1 text-amber-700 dark:text-amber-400"
        title={`${serial ?? ''} — удалена с устройства ${formatDate(entry.missing_since)}`}
      >
        <XCircle className="h-3 w-3" />
        {serialTail(serial)}
      </Badge>
    );
  }
  if (entry.employee) {
    return (
      <Badge
        variant="success"
        className="gap-1"
        title={`${serial ?? ''} — сопоставлена`}
      >
        <CheckCircle2 className="h-3 w-3" />
        {serialTail(serial)}
      </Badge>
    );
  }
  return (
    <Badge variant="warning" title={`${serial ?? ''} — не сопоставлена`}>
      {serialTail(serial)}
    </Badge>
  );
}

export function BioTerminalCards() {
  const { can } = usePermissions();
  const canEdit = can('attendance', 'edit');

  const [terminalId, setTerminalId] = useState('');
  const [onlyUnmatched, setOnlyUnmatched] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedCard, setSelectedCard] = useState<BioTerminalCard | null>(null);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [lastResult, setLastResult] = useState<MapBioTerminalCardResult | null>(
    null,
  );

  const { data: terminals } = useBioTerminals();
  const { data, isLoading, error } = useBioTerminalCards({
    terminal_id: terminalId || undefined,
    unmatched: onlyUnmatched || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const items = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const openMapDialog = (card: BioTerminalCard) => {
    setSelectedCard(card);
    setMapDialogOpen(true);
  };

  const mappedTerminals =
    lastResult?.terminals.filter((t) => t.status === 'mapped') ?? [];
  const skippedTerminals =
    lastResult?.terminals.filter((t) => t.status === 'skipped') ?? [];

  return (
    <div className="space-y-6">
      <Breadcrumbs />

      <div>
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
          <IdCard className="h-8 w-8" />
          Карточки терминалов
        </h1>
        <p className="mt-2 text-muted-foreground">
          Пользователи, заведённые прямо на устройстве. Пока карточка не
          сопоставлена с сотрудником, её проходы не попадают в табель. Человек,
          заведённый на нескольких терминалах под одним номером, показан одной
          строкой и сопоставляется сразу на всех.
        </p>
      </div>

      {lastResult && (
        <div className="space-y-1 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900 dark:border-green-900 dark:bg-green-950 dark:text-green-100">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              Карточка {lastResult.card.terminal_employee_no} сопоставлена на
              терминалах: {mappedTerminals.length}. Разобрано накопленных
              событий: {lastResult.backfill.events}, добавлено отметок в табель:{' '}
              {lastResult.backfill.projected}.
            </span>
          </div>
          {skippedTerminals.map((t) => (
            <div
              key={t.card_id}
              className="flex items-start gap-2 text-amber-800 dark:text-amber-300"
            >
              <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span className="break-all">
                Пропущен терминал {t.terminal.serial_number}: {t.reason}
              </span>
            </div>
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ScanFace className="h-5 w-5" />
            Список карточек
          </CardTitle>
          <CardDescription>
            Список приходит с устройства через агента. Если карточки нет —
            агент ещё не выгружал список пользователей терминала.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Фильтры */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={terminalId}
              onChange={(e) => {
                setTerminalId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Все терминалы</option>
              {(terminals ?? []).map((terminal) => (
                <option key={terminal.id} value={terminal.id}>
                  {terminalLabel(terminal)}
                </option>
              ))}
            </select>

            <Button
              variant={onlyUnmatched ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setOnlyUnmatched((value) => !value);
                setPage(1);
              }}
            >
              Только несопоставленные
            </Button>

            {data && (
              <span className="text-sm text-muted-foreground">
                Человек: {data.total}
              </span>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-10">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="rounded-md bg-red-50 p-4 text-sm text-red-800">
              Не удалось загрузить карточки:{' '}
              {(error as { message?: string })?.message || 'неизвестная ошибка'}
            </div>
          ) : items.length === 0 ? (
            <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              {onlyUnmatched
                ? 'Несопоставленных карточек нет — все пользователи устройства привязаны к сотрудникам.'
                : 'Карточек пока нет.'}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Номер на устройстве</TableHead>
                      <TableHead>Имя на устройстве</TableHead>
                      <TableHead>Терминалы</TableHead>
                      <TableHead>Сотрудник</TableHead>
                      <TableHead>Замечен</TableHead>
                      <TableHead className="text-right">Действие</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((card) => {
                      const unmatchedCount = card.cards.filter(
                        (entry) => !entry.employee,
                      ).length;
                      return (
                        <TableRow key={card.id}>
                          <TableCell className="font-mono text-xs break-all">
                            {card.terminal_employee_no}
                            {card.has_face === false && (
                              <Badge variant="outline" className="ml-2">
                                без лица
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{card.name || '—'}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {card.cards.map((entry) => (
                                <TerminalCardBadge key={entry.id} entry={entry} />
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {card.employee ? (
                              <span className="flex flex-wrap items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                {employeeName(card)}
                                {unmatchedCount > 0 && (
                                  <Badge variant="warning">
                                    не на всех терминалах
                                  </Badge>
                                )}
                              </span>
                            ) : (
                              <Badge variant="warning">Не сопоставлена</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {card.missing_since ? (
                              <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                                <XCircle className="h-3.5 w-3.5" />
                                удалена с устройства{' '}
                                {formatDate(card.missing_since)}
                              </span>
                            ) : (
                              formatDate(card.last_reported_at)
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {unmatchedCount > 0 && canEdit && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openMapDialog(card)}
                              >
                                <Link2 className="mr-2 h-4 w-4" />
                                Сопоставить
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <MapTerminalCardDialog
        open={mapDialogOpen}
        onOpenChange={setMapDialogOpen}
        card={selectedCard}
        onMapped={setLastResult}
      />
    </div>
  );
}
